import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getOrganizerProfile } from '../../../api';
import { useAuth } from '../../../context/AuthContext';

const Onboarding = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { refreshUser } = useAuth();
    
    // Determine status based on URL path
    const getStatus = () => {
        const path = location.pathname.toLowerCase();
        if (path.includes('/success')) return 'success';
        if (path.includes('/failure')) return 'failure';
        if (path.includes('/refresh')) return 'refresh';
        return 'unknown';
    };

    const status = getStatus();

    // Refresh user data after successful onboarding
    useEffect(() => {
        if (status === 'success') {
            // Refresh user profile to get updated Stripe status
            const refreshData = async () => {
                try {
                    await getOrganizerProfile();
                    refreshUser();
                } catch (error) {
                    console.error('Error refreshing user data:', error);
                }
            };
            refreshData();
        }
    }, [status, refreshUser]);

    // Render based on status
    const renderContent = () => {
        switch (status) {
            case 'success':
                return {
                    iconClass: 'fa-solid fa-circle-check',
                    title: 'Stripe Onboarding Successful!',
                    message: 'Your Stripe Connect account has been successfully set up. You can now receive payouts.',
                    bgColor: '#d4edda',
                    borderColor: '#28a745',
                    textColor: '#155724',
                    iconColor: '#28a745'
                };
            case 'failure':
                return {
                    iconClass: 'fa-solid fa-circle-xmark',
                    title: 'Stripe Onboarding Failed',
                    message: 'There was an issue completing your Stripe onboarding. Please try again or contact support if the problem persists.',
                    bgColor: '#f8d7da',
                    borderColor: '#dc3545',
                    textColor: '#721c24',
                    iconColor: '#dc3545'
                };
            case 'refresh':
                return {
                    iconClass: 'fa-solid fa-arrow-rotate-right',
                    title: 'Onboarding Session Expired',
                    message: 'Your onboarding session has expired. Please click the button below to start a new onboarding session.',
                    bgColor: '#fff3cd',
                    borderColor: '#ffc107',
                    textColor: '#856404',
                    iconColor: '#ffc107'
                };
            default:
                return {
                    iconClass: 'fa-solid fa-circle-question',
                    title: 'Unknown Status',
                    message: 'Unable to determine onboarding status.',
                    bgColor: '#e2e3e5',
                    borderColor: '#6c757d',
                    textColor: '#383d41',
                    iconColor: '#6c757d'
                };
        }
    };

    const content = renderContent();

    const handleGoToPayouts = () => {
        navigate('/payouts');
    };

    const handleRetry = () => {
        navigate('/payouts');
    };

    return (
        <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '80vh',
            padding: '20px'
        }}>
            <div style={{
                maxWidth: '600px',
                width: '100%',
                padding: '40px',
                backgroundColor: content.bgColor,
                border: `2px solid ${content.borderColor}`,
                borderRadius: '10px',
                textAlign: 'center',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
            }}>
                {/* Icon */}
                <div style={{
                    fontSize: '64px',
                    color: content.iconColor,
                    marginBottom: '20px'
                }}>
                    <i className={content.iconClass}></i>
                </div>

                {/* Title */}
                <h1 style={{
                    color: content.textColor,
                    marginBottom: '15px',
                    fontSize: '28px',
                    fontWeight: '600'
                }}>
                    {content.title}
                </h1>

                {/* Message */}
                <p style={{
                    color: content.textColor,
                    marginBottom: '30px',
                    fontSize: '16px',
                    lineHeight: '1.6'
                }}>
                    {content.message}
                </p>

                {/* Action Buttons */}
                <div style={{
                    display: 'flex',
                    gap: '15px',
                    justifyContent: 'center',
                    flexWrap: 'wrap'
                }}>
                    {status === 'success' && (
                        <button
                            onClick={handleGoToPayouts}
                            style={{
                                padding: '12px 30px',
                                backgroundColor: '#28a745',
                                color: 'white',
                                border: 'none',
                                borderRadius: '5px',
                                fontSize: '16px',
                                cursor: 'pointer',
                                fontWeight: '500',
                                transition: 'background-color 0.3s'
                            }}
                            onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#218838'}
                            onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#28a745'}
                        >
                            Go to Payouts
                        </button>
                    )}
                    {(status === 'failure' || status === 'refresh') && (
                        <>
                            <button
                                onClick={handleRetry}
                                style={{
                                    padding: '12px 30px',
                                    backgroundColor: '#007bff',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '5px',
                                    fontSize: '16px',
                                    cursor: 'pointer',
                                    fontWeight: '500',
                                    transition: 'background-color 0.3s'
                                }}
                                onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#0056b3'}
                                onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#007bff'}
                            >
                                Try Again
                            </button>
                            <button
                                onClick={handleGoToPayouts}
                                style={{
                                    padding: '12px 30px',
                                    backgroundColor: '#6c757d',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '5px',
                                    fontSize: '16px',
                                    cursor: 'pointer',
                                    fontWeight: '500',
                                    transition: 'background-color 0.3s'
                                }}
                                onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#5a6268'}
                                onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#6c757d'}
                            >
                                Go to Payouts
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Onboarding;